/**
 * Single source of truth for how a `rate` (percentage) discount is displayed.
 *
 * The `discount` field reaches the UI through two independent paths that do not
 * agree on precision:
 *  - product grid  -> `selectedChild.discount`, straight off the listing resource
 *  - product page  -> `getPrise("discount")`, off the details / unit /
 *                     selling-price resource via `calculatPricesForSpecialPrices`
 *
 * Both used to interpolate the raw value, so the same product could read
 * `-33%` on a card and `-33.33%` on its details page. Every percentage badge
 * must go through here instead.
 *
 * Flip `DISCOUNT_PERCENT_DECIMALS` to change the policy everywhere at once.
 */
export const DISCOUNT_PERCENT_DECIMALS = 0;

/** Rounds the API discount to the display precision. `null` when there is nothing to show. */
export const normalizeDiscountPercent = (
  discount?: number | string | null,
): number | null => {
  const value = typeof discount === "string" ? Number(discount) : discount;
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  if (value <= 0) return null;

  const round = (decimals: number) => {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  };

  const rounded = round(DISCOUNT_PERCENT_DECIMALS);
  // A sub-half-percent discount must not collapse into a "-0%" badge.
  return rounded === 0 ? round(DISCOUNT_PERCENT_DECIMALS + 2) || null : rounded;
};

/** Badge text for a rate discount, e.g. `-33%`. `null` when there is nothing to show. */
export const formatDiscountPercent = (
  discount?: number | string | null,
): string | null => {
  const value = normalizeDiscountPercent(discount);
  return value === null ? null : `-${value}%`;
};
